import * as Either from "fp-ts/lib/Either";
import * as Option from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import type { Errors } from "io-ts";
import { PathReporter } from "io-ts/lib/PathReporter";
import type { ChangeEvent } from "react";
import React from "react";
import { useNavigate } from "react-router";
import t from "../../../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import Checkbox from "@ui/Checkbox";
import Dropzone from "@ui/DropzoneV2";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import { parseDashboardFile } from "./parser/dashboard";
import { useDashboardImportCallback } from "../../hook/action/import";
import type { DashboardImportT } from "../../schema/dashboard";
import { IdentifierMapping, useIdentifierMapping } from "../IdentifierMapping";
import { useTranslations } from "../../../../../api/useTranslations";
import { toPath } from "../../../../../router/helpers/path";
import {
  getEmbeddedImageMimeType,
  isEmbeddedImageDataUrl,
  isEmbeddedImageDataUrlCandidate,
} from "../../../../../lib/imageDataUrl";
import AdditionnalMessage from "@ui/AdditionnalMessage";

const SUPPORTED_EMBEDDED_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/svg+xml",
]);

function hasMalformedEmbeddedImages(dashboard: DashboardImportT) {
  return dashboard.blocks.some(block => {
    if ((block.kind !== "DIAGRAM" && block.kind !== "IMAGE") || !block.config.url) return false;

    const mimeType = getEmbeddedImageMimeType(block.config.url);

    return (
      isEmbeddedImageDataUrlCandidate(block.config.url) &&
      (!isEmbeddedImageDataUrl(block.config.url) ||
        (mimeType !== null && !SUPPORTED_EMBEDDED_IMAGE_MIME_TYPES.has(mimeType)))
    );
  });
}

interface ModalImportDashboardProps {
  projectId: string;
  dashboardId?: string;
  userId: string;
  opened: boolean;
  canReplaceCurrentDashboard?: boolean;
  canBePublished?: boolean;
  onClose: () => void;
}

export const DashboardImportModal = React.memo(
  ({
    projectId,
    dashboardId,
    userId,
    opened,
    canReplaceCurrentDashboard = false,
    canBePublished = false,
    onClose,
  }: ModalImportDashboardProps) => {
    const translations = useTranslations();
    const navigate = useNavigate();
    const { addToast } = useToasts();
    const [errors, setErrors] = React.useState<Errors>();
    const [errorText, setErrorText] = React.useState<string>();
    const [fileName, setFileName] = React.useState<string>();
    const [dashboardContent, setFileContent] = React.useState<DashboardImportT>();
    const [hasImportWarnings, setHasImportWarnings] = React.useState(false);
    const [shouldReplaceCurrentDashboard, setShouldReplaceCurrentDashboard] =
      React.useState<boolean>(false);
    const [shouldPublishDashboard, setShouldPublishDashboard] = React.useState<boolean>(false);
    const importDashboardCallback = useDashboardImportCallback(
      projectId,
      userId,
      shouldPublishDashboard,
      dashboardId,
    );
    const { identifierMapping, registerIdentifierMapping, applyIdentifierMapping } =
      useIdentifierMapping();

    const isImportValid = React.useMemo(
      () =>
        Array.isArray(errors) && errors.length > 0 ? false : dashboardContent ? true : undefined,
      [errors, dashboardContent],
    );

    const onReplaceCheckboxChange = React.useCallback(
      (e: ChangeEvent<HTMLInputElement>) => setShouldReplaceCurrentDashboard(e.target.checked),
      [],
    );

    const onPublishCheckboxChange = React.useCallback(
      (e: ChangeEvent<HTMLInputElement>) => setShouldPublishDashboard(e.target.checked),
      [],
    );

    /**
     * @description
     * Reset state when modal is closed (after animation)
     * to prevent browser layout & recalculed styles.
     */
    const onAfterClose = React.useCallback(() => {
      setErrors(undefined);
      setErrorText(undefined);
      setFileName(undefined);
      setFileContent(undefined);
      setHasImportWarnings(false);
      setShouldReplaceCurrentDashboard(false);
    }, []);

    /**
     * @description
     * Callback to handle the file drop event.
     * Using fp-ts Either to handle result of parsing the file and state setup or reset.
     */
    const onFileDrop = React.useCallback(
      ([file]: File[]) =>
        pipe(
          file ? Option.some(file) : Option.none,
          Either.fromOption(() => t("project.dashboards.import.errors.drop")),
          Either.match(
            /** @description Failed drop */
            e => void addToast("error", e),

            /** @description Successful drop */
            f =>
              void parseDashboardFile(f)
                .then(result => {
                  setFileName(f.name);
                  setErrors(() => (Either.isLeft(result) ? result.left : undefined));
                  setErrorText(() =>
                    Either.isLeft(result) ? PathReporter.report(result)[0] : undefined,
                  );
                  if (Either.isLeft(result)) {
                    setFileContent(undefined);
                    setHasImportWarnings(false);
                    return;
                  }

                  setHasImportWarnings(hasMalformedEmbeddedImages(result.right));
                  setFileContent(() => result.right);
                })
                /** @description Unexpected error fall into Dropzone error component */
                .catch(err => {
                  setFileName(f.name);
                  setErrors(() => [err]);
                  setErrorText(() => err.message);
                  setFileContent(undefined);
                  setHasImportWarnings(false);
                }),
          ),
        ),
      [addToast],
    );

    const onFormSubmit = React.useCallback(async () => {
      if (!dashboardContent || !projectId) return false;

      const mappedDashboardContent =
        Object.keys(identifierMapping).length > 0
          ? applyIdentifierMapping(dashboardContent)
          : dashboardContent;

      if (dashboardId) {
        try {
          const { hasWarnings } = await importDashboardCallback(mappedDashboardContent);
          setHasImportWarnings(hasWarnings);
          return !hasWarnings;
        } catch (err) {
          addToast(
            "error",
            err instanceof Error ? err.message : `${err}`,
            t("project.dashboards.import.fail", translations),
          );
          return false;
        }
      }

      try {
        const { result, hasWarnings } = await importDashboardCallback(mappedDashboardContent);
        if (!result) throw new Error(t("project.dashboards.import.fail", translations));

        addToast("success", t("project.dashboards.import.success", translations));

        if (hasWarnings) setHasImportWarnings(true);
        void navigate(
          toPath("/projects/:projectId/dashboards/:dashboardId", {
            projectId,
            dashboardId: result.dashboard.id,
          }),
        );
        return true;
      } catch (err) {
        addToast(
          "error",
          err instanceof Error ? err.message : `${err}`,
          t("project.dashboards.import.fail", translations),
        );
        return false;
      }
    }, [
      dashboardId,
      translations,
      dashboardContent,
      projectId,
      identifierMapping,
      applyIdentifierMapping,
      importDashboardCallback,
      addToast,
      navigate,
    ]);

    return (
      <Modal
        title={t("project.dashboards.import.modal.title", translations)}
        // subtitle={t("project.dashboards.import.modal.subtitle")}
        isOpen={opened}
        onRequestClose={onClose}
        onAfterClose={onAfterClose}
        size="xl"
      >
        <ModalForm
          onSubmit={onFormSubmit}
          onRequestClose={onClose}
          okButtonName={t("project.dashboards.import.action.validation")}
        >
          <Dropzone
            validation={isImportValid}
            errorText={errorText}
            nameFile={fileName}
            onDrop={onFileDrop}
            accept=".json"
            pathSampleFile="/metroscopia_example_dashboard.json"
          />
          {hasImportWarnings && (
            <AdditionnalMessage
              status="WARNING"
              text={t("project.dashboards.import.warning.title")}
            />
          )}
          {canReplaceCurrentDashboard && (
            <Checkbox checked={shouldReplaceCurrentDashboard} onChange={onReplaceCheckboxChange}>
              {t("project.dashboards.import.options.replace", translations)}
            </Checkbox>
          )}
          {canBePublished && (
            <Checkbox checked={shouldPublishDashboard} onChange={onPublishCheckboxChange}>
              {t("project.dashboards.import.options.publish", translations)}
            </Checkbox>
          )}
          {dashboardContent && (
            <IdentifierMapping
              projectId={projectId}
              dashboard={dashboardContent}
              identifierMapping={identifierMapping}
              registerIdentifierMapping={registerIdentifierMapping}
            />
          )}
        </ModalForm>
      </Modal>
    );
  },
);
